
import { useEffect } from "react";
import { Header } from "@/components/Header";
import { Footer } from "@/components/Footer";
import { SectionTitle } from "@/components/SectionTitle";
import { CTASection } from "@/components/CTASection";
import { Button } from "@/components/ui/button";
import { TreatmentServiceCard } from "@/components/TreatmentServiceCard";
import { FAQAccordion } from "@/components/FAQAccordion";
import { ServiceTestimonials } from "@/components/ServiceTestimonials";
import { ArrowRight, CheckCircle2 } from "lucide-react";
import { Link } from "react-router-dom";
import { PageTitle } from "@/components/PageTitle";

const FullMouthImplants = () => {
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  // FAQs specific to Full Mouth Rehabilitation and Implants
  const faqs = [
    {
      question: "What is full mouth rehabilitation?",
      answer: "Full mouth rehabilitation is a comprehensive treatment approach that restores all teeth in both the upper and lower jaws. It addresses multiple dental issues simultaneously, including missing teeth, worn or damaged teeth, misalignment, and bite problems to restore both function and aesthetics."
    },
    {
      question: "Are dental implants painful?",
      answer: "The implant procedure is performed under local anesthesia, so you shouldn't feel pain during the surgery. After the procedure, some discomfort is normal but is typically manageable with prescribed or over-the-counter pain medications. Most patients report that implant surgery is less uncomfortable than they anticipated."
    },
    {
      question: "How long do dental implants last?",
      answer: "With proper care and maintenance, dental implants can last a lifetime. The implant itself (the titanium post) has a success rate of over 95% after 10 years. The crown placed on top of the implant may need replacement after 10-15 years due to normal wear and tear."
    },
    {
      question: "Am I a good candidate for dental implants?",
      answer: "Most adults with good general health are candidates for dental implants. Key factors include having adequate bone density in your jaw to support the implant, healthy gums free of periodontal disease, and good overall health. During your consultation, we'll conduct a thorough evaluation to determine if implants are right for you."
    },
    {
      question: "How long does the full mouth rehabilitation process take?",
      answer: "The timeline varies depending on the complexity of your case and the treatments needed. Simple cases might be completed in a few months, while more complex rehabilitations involving multiple procedures could take 6-12 months or longer. For cases involving dental implants, the process includes healing time between stages."
    }
  ];

  // Testimonials for Full Mouth Rehabilitation and Implants
  const testimonials = [
    {
      name: "Rajiv Khanna",
      comment: "After years of dental problems and missing teeth, Dr. Asrani restored my entire mouth with implants and crowns. I can now eat anything without pain or embarrassment. The team was compassionate and professional throughout my treatment journey, making a complex process feel manageable.",
      rating: 5
    },
    {
      name: "Sarita Agarwal",
      comment: "I had severe tooth wear from grinding and multiple missing teeth. The full mouth rehabilitation at Asrani Dental not only restored my smile but also corrected my bite. The difference in both appearance and function is remarkable. I feel like I have a whole new lease on life!",
      rating: 5
    },
    {
      name: "Deepak Verma",
      comment: "Getting implants at Asrani Dental was one of the best decisions I've made. After losing several teeth due to an accident, I was hesitant about extensive dental work. Dr. Asrani explained everything thoroughly and created a step-by-step plan that made me comfortable with the process. The results are beyond my expectations!",
      rating: 5
    }
  ];

  const handleBookAppointment = () => {
    window.open("https://www.healthcoco.com/nagpur/doctor/dr-hemant-asrani-endodontist-dentist/USRH249D644", "_blank");
  };

  return (
    <div className="min-h-screen">
      <PageTitle title="Full Mouth & Implants in Nagpur | Cost of Full Mouth & Implants in Nagpur" />
      <Header />
      
      {/* Hero Section */}
      <section className="pt-32 pb-20 md:pt-40 md:pb-32 bg-dental-offwhite">
        <div className="container-custom">
          <div className="max-w-3xl mx-auto text-center">
            <span className="inline-block text-dental-highlight font-medium mb-4 uppercase tracking-wider text-sm">
              Our Services
            </span>
            <h1 className="text-4xl md:text-5xl lg:text-6xl font-serif font-semibold mb-6">
              Full Mouth & Implants in Nagpur
            </h1>
            <p className="text-lg text-dental-muted">
              Comprehensive solutions to restore your complete smile with advanced implant technology and full mouth rehabilitation.
            </p>
            <div className="mt-8">
              <Button className="btn-primary" onClick={handleBookAppointment}>
                Book Appointment
              </Button>
            </div>
          </div>
        </div>
      </section>
      
      {/* Services Overview */}
      <section className="py-16 md:py-24 bg-white">
        <div className="container-custom">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <div>
              <img 
                src="/lovable-uploads/completesmilerestoration.jpg" 
                alt="Full Mouth Rehabilitation and Implants at Asrani Dental Clinic" 
                className="rounded-xl shadow-soft w-full h-auto"
              />
            </div>
            
            <div>
              <SectionTitle 
                title="Complete Smile Restoration" 
                subtitle="Full Mouth & Implants"
                description="Our full mouth rehabilitation and implant services provide comprehensive solutions for patients with extensive dental needs, restoring both function and aesthetics."
                alignment="left"
              />
              
              <div className="mt-8 space-y-4">
                <div className="flex items-start">
                  <CheckCircle2 size={24} className="text-dental-highlight mr-3 mt-1 flex-shrink-0" />
                  <div>
                    <h3 className="text-lg font-medium mb-1">Permanent Solutions</h3>
                    <p className="text-dental-muted">Dental implants provide a lifelong foundation for replacement teeth that look, feel, and function like natural teeth.</p>
                  </div>
                </div>
                
                <div className="flex items-start">
                  <CheckCircle2 size={24} className="text-dental-highlight mr-3 mt-1 flex-shrink-0" />
                  <div>
                    <h3 className="text-lg font-medium mb-1">Comprehensive Approach</h3>
                    <p className="text-dental-muted">We address all aspects of your dental health simultaneously, from missing teeth to bite problems.</p>
                  </div>
                </div>
                
                <div className="flex items-start">
                  <CheckCircle2 size={24} className="text-dental-highlight mr-3 mt-1 flex-shrink-0" />
                  <div>
                    <h3 className="text-lg font-medium mb-1">Personalized Treatment</h3>
                    <p className="text-dental-muted">Every full mouth rehabilitation plan is custom-designed for your specific needs and goals.</p>
                  </div>
                </div>
                
                <div className="flex items-start">
                  <CheckCircle2 size={24} className="text-dental-highlight mr-3 mt-1 flex-shrink-0" />
                  <div>
                    <h3 className="text-lg font-medium mb-1">Improved Quality of Life</h3>
                    <p className="text-dental-muted">Restore your ability to eat, speak, and smile with confidence while preventing bone loss.</p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>
      
      {/* Treatments Section */}
      <section className="py-16 md:py-24 bg-dental-cream">
        <div className="container-custom">
          <SectionTitle 
            title="Our Full Mouth & Implant Treatments" 
            subtitle="Services We Offer"
            description="We provide comprehensive solutions for restoring your complete smile, from single implants to full mouth rehabilitation."
            alignment="center"
          />
          
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 mt-12">
            <TreatmentServiceCard 
              title="Single Tooth Implants"
              description="Replace individual missing teeth with dental implants that look and function like natural teeth without affecting adjacent teeth."
              iconImage="/lovable-uploads/285554d6-4566-4a60-9073-9f9864390211.png"
              iconAlt="Single tooth implant icon"
            />
            
            <TreatmentServiceCard 
              title="Multiple Tooth Implants"
              description="Replace several missing teeth with implant-supported bridges or multiple individual implants for optimal function and aesthetics."
              iconImage="/lovable-uploads/f6ace886-837e-4e46-9380-939e9886efaa.png"
              iconAlt="Multiple tooth implants icon"
            />
            
            <TreatmentServiceCard 
              title="Full Arch Rehabilitation"
              description="Comprehensive restoration of an entire arch of teeth using implant-supported dentures or fixed bridges."
              iconImage="/lovable-uploads/36de54eb-692e-4f11-9bb1-9c76c7e6615a.png"
              iconAlt="Full arch rehabilitation icon"
            />
            
            <TreatmentServiceCard 
              title="All-on-4 Implants"
              description="Revolutionary technique providing a full arch of teeth supported by just four strategically placed implants, often completed in a single day."
              iconImage="/lovable-uploads/26458846-8adc-4cfd-9f16-a624fe39cbdb.png"
              iconAlt="All-on-4 implants icon"
            />
            
            <TreatmentServiceCard 
              title="Implant-Supported Dentures"
              description="Secure dentures with implant attachments for improved stability, comfort, and chewing efficiency compared to traditional dentures."
              iconImage="/lovable-uploads/1bbf191d-ec90-4f71-9f8e-8391475ee0ea.png"
              iconAlt="Implant-supported dentures icon"
            />
            
            <TreatmentServiceCard 
              title="Bone Grafting"
              description="Procedures to build up the jawbone in areas with insufficient bone volume to support dental implants."
              iconImage="/lovable-uploads/ea3b5441-00b2-4f4d-a421-8aa463145b7c.png"
              iconAlt="Bone grafting icon"
            />
          </div>
        </div>
      </section>
      
      {/* Testimonials Section */}
      <section className="py-16 md:py-24 bg-white">
        <div className="container-custom">
          <SectionTitle 
            title="Patient Testimonials" 
            subtitle="What Our Patients Say"
            description="Hear from our patients about their experiences with our full mouth rehabilitation and implant services."
            alignment="center"
          />
          
          <div className="max-w-4xl mx-auto mt-12">
            <ServiceTestimonials testimonials={testimonials} />
          </div>
        </div>
      </section>
      
      {/* FAQ Section */}
      <section className="py-16 md:py-24 bg-dental-offwhite">
        <div className="container-custom">
          <SectionTitle 
            title="Frequently Asked Questions" 
            subtitle="Common Questions"
            description="Find answers to common questions about full mouth rehabilitation and dental implants."
            alignment="center"
          />
          
          <div className="max-w-3xl mx-auto mt-12">
            <FAQAccordion faqs={faqs} />
          </div>
        </div>
      </section>
      
      {/* Next Steps Section */}
      <section className="py-16 md:py-24 bg-white">
        <div className="container-custom">
          <div className="max-w-3xl mx-auto text-center">
            <h2 className="text-3xl md:text-4xl font-serif font-semibold mb-6">Ready to Restore Your Complete Smile?</h2>
            <p className="text-lg text-dental-muted mb-8">
              Contact our office today to schedule your consultation for full mouth rehabilitation or dental implants. Let us help you regain a functional, beautiful smile.
            </p>
            
            <div className="flex flex-col sm:flex-row justify-center gap-4">
              <Button className="btn-primary flex items-center text-base group" onClick={handleBookAppointment}>
                Book Appointment
                <ArrowRight size={18} className="ml-2 transition-transform duration-300 group-hover:translate-x-1" />
              </Button>
              
              <Button className="btn-secondary flex items-center text-base" onClick={() => window.location.href = '/contact'}>
                Contact Us
              </Button>
            </div>
            
            <div className="mt-10">
              <p className="text-dental-muted mb-4">Interested in our other dental services?</p>
              <div className="flex flex-wrap justify-center gap-4">
                <Link to="/services/general-dentistry-in-nagpur" className="text-dental-highlight hover:text-dental-accent transition-colors">
                  General Dentistry
                </Link>
                <Link to="/services/cosmetic-dentistry" className="text-dental-highlight hover:text-dental-accent transition-colors">
                  Cosmetic Dentistry
                </Link>
                <Link to="/services/orthodontics-in-nagpur" className="text-dental-highlight hover:text-dental-accent transition-colors">
                  Orthodontics
                </Link>
                <Link to="/services/painless-rct-in-nagpur" className="text-dental-highlight hover:text-dental-accent transition-colors">
                  Painless RCT
                </Link>
                <Link to="/services/pediatric-dentistry-in-nagpur" className="text-dental-highlight hover:text-dental-accent transition-colors">
                  Pediatric Dentistry
                </Link>
              </div>
            </div>
          </div>
        </div>
      </section>
      
      <CTASection
        title="Transform Your Dental Health"
        description="Our advanced full mouth rehabilitation and implant services can help you regain a complete, functional smile. Schedule your consultation today."
        primaryButtonText="Book an Appointment"
        secondaryButtonText="Learn More"
        primaryButtonAction={handleBookAppointment}
        secondaryButtonAction={() => window.location.href = "/about"}
      />
      
      <Footer />
    </div>
  );
};

export default FullMouthImplants;
